<template>
	<section class="aside" :class="{ aside_scrolled: isScrolled }">
		<div class="aside__inner _cnt">
			<Transition>
				<div v-if="products && products.length" class="aside__slider">
					<swiper
						ref="swiperVerticalRef"
						:modules="modules"
						:slides-per-view="'auto'"
						:space-between="10"
						direction="vertical"
						:free-mode="true"
					>
						<swiper-slide v-for="(item, i) in products" :key="i">
							<div
								class="aside__slide"
								@click.stop="$emit('openProductModal', item.id)"
							>
								<div class="aside__bg _ibg-contain">
									<img src="@/assets/images/bg/eagle.png" />
								</div>
								<div class="aside__bg-blur _ibg-contain">
									<img src="@/assets/images/bg/eagle-blur.png" />
								</div>
								<div class="aside__image _ibg-contain">
									<img :src="imgDomain + item.img_url" />
								</div>
								<div class="aside__type">{{ getType(item.title) }}</div>
								<div class="aside__title">
									{{ getTitle(item.title) }}
								</div>
							</div>
						</swiper-slide>
					</swiper>
				</div>
			</Transition>
		</div>
	</section>
</template>

<script>
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { mapGetters } from 'vuex';

export default {
	name: 'ProductCarusel',
	components: {
		Swiper,
		SwiperSlide,
	},
	props: {
		isAuth: {
			type: Boolean,
			required: true,
		},
		chosenItem: {
			type: String,
		},
	},
	data() {
		return {
			imgDomain: '',
			products: [],
			swiperVerticalRef: null,
			modules: [Navigation],
			isScrolled: false,
		};
	},
	watch: {
		currencyCode() {
			this.fetchProducts();
		},
	},
	computed: {
		...mapGetters('app', ['currencyCode', 'currencySymbol']),
	},
	mounted() {
		this.fetchProducts();
		this.$nextTick(() => {
			if (
				this.$refs.swiperVerticalRef &&
				this.$refs.swiperVerticalRef.swiper
			) {
				this.$refs.swiperVerticalRef.swiper.on('init', () => {
					this.swiperVerticalRef = this.$refs.swiperVerticalRef.swiper;
				});
			}
		});
		this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
		this._onScroll = () => {
			this.isScrolled = window.pageYOffset > 1;
		};
		this._onScroll();
		window.addEventListener('scroll', this._onScroll, { passive: true });
	},
	beforeUnmount() {
		window.removeEventListener('scroll', this._onScroll);
	},
	methods: {
		addToCart(item) {
			this.$emit('addToCart', item);
		},

		fetchProducts() {
			const base = process.env.VUE_APP_API;
			const url = `${base}items/list`;

			this.products = [];

			this.$http
				.get(url, {
					params: {
						random: true,
						limit: 30,
						currency: this.currencyCode || 'EUR',
						lang_id: 10,
					},
				})
				.then((res) => {
					const payload = res?.data?.payload;

					if (Array.isArray(payload)) {
						this.products = payload;
					} else {
						console.error(
							'Unexpected /items/list response, expected payload array:',
							res.data
						);
						this.products = [];
					}
				})
				.catch((err) => {
					console.error('Failed to fetch products:', err);

					if (err.response && err.response.status === 401) {
						this.openSignInModal?.();
					}
					this.products = [];
				});
		},
		goToProduct(item) {
			this.$emit('goToProduct', item);
		},
		slidePrev() {
			if (this.swiperVerticalRef) {
				this.swiperVerticalRef.slidePrev();
			}
		},
		slideNext() {
			if (this.swiperVerticalRef) {
				this.swiperVerticalRef.slideNext();
			}
		},
		getTitle(title) {
			if (!title) return '';
			let part = title.split('|')[1] || title;
			part = part.split('(')[0];
			return part.trim();
		},
		getType(title) {
			if (!title) return '';
			let part = title.split('|')[0];
			part = part.replace(/^(StatTrak™|Souvenir)\s+/i, '');
			return part.trim();
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

//========================================================================================================================================================

.aside {
	height: 100vh;
	max-height: 100vh;
	position: fixed;
	z-index: 3;
	min-height: 100%;
	@include adaptiveValue('min-width', 280, 140, 1920, 992, 1);
	@include adaptiveValue('max-width', 280, 140, 1920, 992, 1);
	margin-top: 0px;
	transition: all 0.3s ease 0s;
	@media (max-width: $md2) {
		@include hide-item;
	}
	&_scrolled {
		@media (min-width: $md1) {
			margin-top: -25px;
		}
	}
	&__inner {
		padding: 0px;
		height: 100%;
	}
	&__slider {
		height: 100%;
		overflow: hidden;
	}
	&__slider .swiper {
		height: 100%;
	}
	&__slide {
		cursor: pointer;
		background-color: var(--bg-secondary-color);
		position: relative;
		padding-right: 10px;
		padding-bottom: 10px;
		@include adaptiveValue('min-height', 160, 130, 1920, 992, 1);
		@include adaptiveValue('padding-left', 25, 15, 1920, 992, 1);
		@include adaptiveValue('min-width', 280, 140, 1920, 992, 1);
		@include adaptiveValue('max-width', 280, 140, 1920, 992, 1);
		position: relative;
		@media (any-hover: hover) {
			&:hover {
				.aside__image {
					transform: scale(1.1);
				}
			}
		}
		&::before {
			content: '';
			position: absolute;
			@include adaptiveValue('width', 10, 5, 1920, 992, 1);
			height: 100%;
			top: 0;
			left: 0;
			background-color: var(--hint-color);
		}
	}
	&__bg,
	&__bg-blur {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
	}
	&__image {
		position: relative;
		margin: 0 auto;
		width: 100%;
		@include adaptiveValue('min-width', 180, 140, 1920, 992, 1);
		padding-bottom: 75%;
		margin-bottom: -24px;
		@include adaptiveValue('margin-bottom', -24, 5, 1920, 992, 1);
		transform: scale(1);
		transition: transform 0.3s ease 0s;
	}
	&__type,
	&__title {
		@include adaptiveValue('font-size', 16, 14, 1920, 992, 1);
		@include adaptiveValue('line-height', 24, 20, 1920, 992, 1);
		font-weight: 500;
		font-family: var(--font-family-inter);
	}
	&__type {
		color: var(--secondary-color);
	}
	&__title {
		max-width: 80%;
		@include adaptiveValue('width', 260, 180, 1920, 992, 1);
	}
}

.swiper-slide {
	@include adaptiveValue('min-width', 280, 200, 1920, 992, 1);
	flex-shrink: 1 !important;
	display: flex;
	height: auto !important;
	margin-right: 0px !important;
	margin-bottom: 2px !important;
}
</style>
